<template>
	<div class="d-flex flex-column align-items-center">
		<!-- The renderer answers with a self-contained <svg>, no script and no external
		     reference, and the only value in it is the otpauth URI the backend just built. -->
		<div class="qr-code bg-white rounded p-2" v-html="enrollment.qr_svg"></div>

		<label class="form-label text-muted small mt-3 mb-1" :for="inputId">
			{{ t('components.two_factor.secret') }}
		</label>
		<div class="input-group input-group-sm" style="max-width: 24rem">
			<input
				:id="inputId"
				:value="enrollment.secret"
				readonly
				class="form-control font-monospace text-center"
				@focus="($event.target as HTMLInputElement).select()"
			/>
			<button
				type="button"
				class="btn btn-outline-secondary"
				:title="t('components.two_factor.copy')"
				@click.prevent="copySecret()"
			>
				<i class="fa fa-fw fa-copy" />
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
/**
 * The QR code of a fresh enrolment with the secret underneath it for manual entry.
 *
 * Shared by the setup dialog on the profile and the enrolment step the login forces; both get
 * the same payload, from `me/two-factor` and `auth/two-factor/setup` respectively.
 *
 * @prop enrollment - What the enrolment endpoint answered, secret and QR code included.
 */
import { useId } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToasterStore } from '@lib/plugins/toasts/toasts';

import type { TwoFactorEnrollment } from '@lib/types';

const props = defineProps<{
	enrollment: TwoFactorEnrollment;
}>();

/*
 * States
 */
const { t } = useI18n();
const toaster = useToasterStore();
const inputId = useId();

/*
 * Functions
 */
function copySecret() {
	navigator.clipboard?.writeText(props.enrollment.secret).then(() => {
		toaster.addSuccessToast(t('components.two_factor.copied'));
	});
}
</script>

<style lang="scss" scoped>
// The code has to stay dark on light whatever the theme does around it, and a fixed width keeps
// the module grid from being resampled into a pattern a scanner reads wrong.
.qr-code :deep(svg) {
	display: block;
	width: 15rem;
	height: 15rem;
}
</style>
